import { InputControl, TextareaControl } from "@shared/components/form";
import LinkFormControl from "@shared/components/form/LinkFormControl";
import { MediaUploader } from "@shared/components/media/MediaUploader";
import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import { MediaFile, MediaType } from "@shared/types/media.types";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { EventMediaType, EventSectionData } from "../../types/home.types";

interface FormValues {
  title: string;
  subtitle: string;
  description: string;
  buttonText: string;
  buttonLink: string;
  buttonTarget: string;
  imagePath: string;
  videoPath: string;
  mediaType: EventMediaType;
  roundedImage: boolean;
  videoAutoplay: boolean;
  videoLoop: boolean;
  videoMuted: boolean;
}

interface EditEventSectionModalProps {
  initialData?: EventSectionData;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<EventSectionData>) => Promise<void>;
  isLoading?: boolean;
}

const EditEventSectionModal: React.FC<EditEventSectionModalProps> = ({
  initialData,
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const initialMediaType: EventMediaType = initialData?.mediaType || "image";

  const [formValues, setFormValues] = useState<FormValues>({
    title: initialData?.title || "",
    subtitle: initialData?.subtitle || "",
    description: initialData?.description || "",
    buttonText: initialData?.buttonText || "",
    buttonLink: initialData?.buttonLink || "",
    buttonTarget: initialData?.buttonTarget || "_self",
    imagePath: initialData?.imagePath || "",
    videoPath: initialData?.videoPath || "",
    mediaType: initialMediaType,
    roundedImage: initialData?.roundedImage ?? true,
    videoAutoplay: initialData?.videoAutoplay ?? false,
    videoLoop: initialData?.videoLoop ?? false,
    videoMuted: initialData?.videoMuted ?? false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>(
    initialData?.imagePath && initialMediaType === "image"
      ? [
          {
            type: MediaType.IMAGE,
            preview: initialData.imagePath,
            uploadedUrl: initialData.imagePath,
            file: new File([], "", { type: "image/jpeg" }),
            progress: 100,
          },
        ]
      : [],
  );

  const [videoFiles, setVideoFiles] = useState<MediaFile[]>(
    initialData?.videoPath && initialMediaType === "video"
      ? [
          {
            type: MediaType.VIDEO,
            preview: initialData.videoPath,
            uploadedUrl: initialData.videoPath,
            file: new File([], "", { type: "video/mp4" }),
            progress: 100,
          },
        ]
      : [],
  );

  // Handle input changes
  const handleChange = (field: keyof FormValues, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user types
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleMediaTypeChange = (type: EventMediaType) => {
    setFormValues((prev) => ({ ...prev, mediaType: type }));
    setErrors((prev) => ({ ...prev, imagePath: "", videoPath: "" }));
  };

  const handleImageUpload = (newMediaFiles: MediaFile[]) => {
    if (newMediaFiles.length > 0) {
      const uploadedUrl =
        newMediaFiles[0].uploadedUrl || newMediaFiles[0].preview;
      setMediaFiles(newMediaFiles);
      setFormValues((prev) => ({ ...prev, imagePath: uploadedUrl }));
      if (errors.imagePath) setErrors((prev) => ({ ...prev, imagePath: "" }));
    }
  };

  const handleRemoveImage = () => {
    setMediaFiles([]);
    setFormValues((prev) => ({ ...prev, imagePath: "" }));
  };

  const handleVideoUpload = (newMediaFiles: MediaFile[]) => {
    if (newMediaFiles.length > 0) {
      if (newMediaFiles[0].type !== MediaType.VIDEO) {
        toast.error("Please upload a video file.");
        return;
      }
      const uploadedUrl =
        newMediaFiles[0].uploadedUrl || newMediaFiles[0].preview;
      setVideoFiles(newMediaFiles);
      setFormValues((prev) => ({ ...prev, videoPath: uploadedUrl }));
      if (errors.videoPath) setErrors((prev) => ({ ...prev, videoPath: "" }));
    }
  };

  const handleRemoveVideo = () => {
    setVideoFiles([]);
    setFormValues((prev) => ({ ...prev, videoPath: "" }));
  };

  // Handle link form changes
  const handleLinkChange = (
    field: "text" | "link" | "target",
    value: string,
  ) => {
    // Map LinkFormControl fields to form fields
    const fieldMap: Record<string, keyof FormValues> = {
      text: "buttonText",
      link: "buttonLink",
      target: "buttonTarget",
    };

    handleChange(fieldMap[field], value);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formValues.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formValues.buttonText.trim()) {
      newErrors.buttonText = "Button text is required";
    }

    if (!formValues.buttonLink.trim()) {
      newErrors.buttonLink = "Button link is required";
    }

    if (formValues.mediaType === "image" && !formValues.imagePath.trim()) {
      newErrors.imagePath = "Image is required";
    }

    if (formValues.mediaType === "video" && !formValues.videoPath.trim()) {
      newErrors.videoPath = "Video is required";
    }

    // Target is optional but should be validated if provided
    if (
      formValues.buttonTarget &&
      !["_self", "_blank", "_parent", "_top", "internal"].includes(
        formValues.buttonTarget,
      )
    ) {
      newErrors.buttonTarget = "Invalid target value";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formValues);
      onClose();
    } catch (error: any) {
      const message =
        error?.response?.data?.error?.message ||
        error?.message ||
        "Failed to save changes. Please try again.";
      toast.error(message);
    }
  };

  return (
    <ModalBlank
      id="edit-event-section-modal"
      modalOpen={isOpen}
      setModalOpen={onClose}
      showCloseButton={true}
      className="max-w-[500px] p-6"
    >
      <div className="mb-4">
        <h2 className="text-lg font-semibold">Edit Event Section</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <InputControl
          id="title"
          label="Title"
          value={formValues.title}
          onChange={(e) => handleChange("title", e.target.value)}
          placeholder="Enter title"
          disabled={isLoading}
          error={errors.title}
          required
        />

        <InputControl
          id="subtitle"
          label="Subtitle"
          value={formValues.subtitle}
          onChange={(e) => handleChange("subtitle", e.target.value)}
          placeholder="Enter subtitle"
          disabled={isLoading}
          error={errors.subtitle}
        />

        <TextareaControl
          id="description"
          label="Description"
          value={formValues.description}
          onChange={(e) => handleChange("description", e.target.value)}
          placeholder="Enter description"
          disabled={isLoading}
          error={errors.description}
        />

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Add Media
          </label>

          {/* Media type selector */}
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {(["image", "video"] as EventMediaType[]).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => handleMediaTypeChange(type)}
                className={`flex-1 py-2 text-sm font-medium transition-colors capitalize ${
                  formValues.mediaType === type
                    ? "bg-blue-600 text-white"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                {type === "image" ? "Image" : "Video"}
              </button>
            ))}
          </div>

          {/* Image uploader */}
          {formValues.mediaType === "image" && (
            <>
              {mediaFiles.length === 0 ? (
                <MediaUploader
                  onUploadComplete={handleImageUpload}
                  onError={(error) => toast.error(error)}
                  maxFiles={1}
                  acceptType="image"
                />
              ) : (
                <div className="relative rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img
                    src={formValues.imagePath}
                    alt="Event"
                    className="w-full h-auto object-cover max-h-[200px]"
                  />
                  <button
                    type="button"
                    className="absolute top-2 right-2 bg-gray-800 bg-opacity-60 rounded-full p-1 text-white"
                    onClick={handleRemoveImage}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
              {errors.imagePath && (
                <p className="text-red-500 text-xs mt-1">{errors.imagePath}</p>
              )}

              {/* Rounded image checkbox — only for image */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={formValues.roundedImage}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      roundedImage: e.target.checked,
                    }))
                  }
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                  disabled={isLoading}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Rounded image
                </span>
              </label>
            </>
          )}

          {/* Video uploader */}
          {formValues.mediaType === "video" && (
            <>
              {videoFiles.length === 0 ? (
                <MediaUploader
                  onUploadComplete={handleVideoUpload}
                  onError={(error) => toast.error(error)}
                  maxFiles={1}
                  acceptType="all"
                />
              ) : (
                <div className="space-y-3">
                  <div className="relative rounded-md overflow-hidden bg-gray-100 dark:bg-gray-700">
                    <video
                      src={formValues.videoPath}
                      className="w-full max-h-[200px] object-cover"
                      controls
                    />
                    <button
                      type="button"
                      className="absolute top-2 right-2 bg-gray-800 bg-opacity-60 rounded-full p-1 text-white"
                      onClick={handleRemoveVideo}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Video playback options */}
                  <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {(
                      [
                        { key: "videoAutoplay", label: "Autoplay" },
                        { key: "videoLoop", label: "Loop" },
                        { key: "videoMuted", label: "Muted" },
                      ] as { key: keyof FormValues; label: string }[]
                    ).map(({ key, label }) => {
                      const isLocked =
                        key === "videoMuted" && formValues.videoAutoplay;
                      const isActive = !!formValues[key];
                      return (
                        <button
                          key={key}
                          type="button"
                          disabled={isLocked}
                          onClick={() => {
                            if (key === "videoAutoplay") {
                              const next = !formValues.videoAutoplay;
                              setFormValues((prev) => ({
                                ...prev,
                                videoAutoplay: next,
                                videoMuted: next ? true : prev.videoMuted,
                              }));
                            } else {
                              setFormValues((prev) => ({
                                ...prev,
                                [key]: !prev[key],
                              }));
                            }
                          }}
                          className={`flex-1 py-2 text-sm font-medium transition-colors ${
                            isActive
                              ? "bg-blue-600 text-white"
                              : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                          } ${isLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {errors.videoPath && (
                <p className="text-red-500 text-xs mt-1">{errors.videoPath}</p>
              )}
            </>
          )}
        </div>

        <LinkFormControl
          idPrefix="button"
          value={{
            text: formValues.buttonText,
            link: formValues.buttonLink,
            target: formValues.buttonTarget,
          }}
          onChange={handleLinkChange}
          disabled={isLoading}
          errors={{
            text: errors.buttonText,
            link: errors.buttonLink,
            target: errors.buttonTarget,
          }}
          required
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </ModalBlank>
  );
};

export default EditEventSectionModal;
